import { useNavigate } from 'react-router-dom';
import type { User, Space } from '@/types';
import { useWorkspace } from '@/context';
import { conversationPathWithWorkflow } from '@/config/routes';
import { APP_BRAND } from '@/config/brand';
import { getBrandConfig } from '@/config/brandConfig';
import { BrandMarkIcon } from '@/components/icons';
import { AssetIcon } from '@/components/icons/AssetIcon';
import { chatIcon, searchIcon, sidebarIcon } from '@/assets/icons';
import { SpacesSection } from './SpacesSection';
import { ChatHistorySection, type ChatHistoryItem } from './ChatHistorySection';

interface SidebarProps {
  user: User;
  spaces: Space[];
  chatHistory: ChatHistoryItem[];
  isOpen: boolean;
  onNewChat: () => void;
  onToggle: () => void;
}

export function Sidebar({ spaces, chatHistory, isOpen, onNewChat, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const { effectiveRole } = useWorkspace();
  const brandConfig = getBrandConfig();
  const isHidBrand = APP_BRAND === 'hid';

  return (
    <aside className={`sidebar${isOpen ? '' : ' sidebar--collapsed'}`}>
      {isOpen ? (
        <>
          {/* Brand — matches Figma: logo at top of full-height left rail */}
          <div className="sidebar__brand">
            {isHidBrand && brandConfig.hidLogo ? (
              <img
                src={brandConfig.hidLogo}
                alt="HID Agent Guard"
                className="sidebar__brand-logo"
                width={120}
                height={50}
              />
            ) : (
              <>
                <div className="sidebar__brand-icon">
                  <BrandMarkIcon size={32} />
                </div>
                <span className="sidebar__brand-name">Agent Guard</span>
              </>
            )}
          </div>

          <section className="sidebar__top">
            <button type="button" className="sidebar__action-btn" onClick={onNewChat}>
              <AssetIcon src={chatIcon} size={20} className="sidebar__action-icon" />
              <span>New chat</span>
            </button>
            <button type="button" className="sidebar__action-btn">
              <AssetIcon src={searchIcon} size={20} className="sidebar__action-icon" />
              <span>Search</span>
            </button>
          </section>

          <SpacesSection spaces={spaces} />

          <ChatHistorySection
            history={chatHistory}
            onSelect={(id) => navigate(conversationPathWithWorkflow(id, 'device-setup', effectiveRole))}
          />
        </>
      ) : (
        <nav className="sidebar__rail" aria-label="Sidebar shortcuts">
          <button
            type="button"
            className="sidebar__rail-btn"
            onClick={onToggle}
            aria-label="Expand sidebar"
          >
            <AssetIcon src={sidebarIcon} size={20} className="sidebar__rail-icon" />
          </button>
          <button type="button" className="sidebar__rail-btn" onClick={onNewChat} aria-label="New chat">
            <AssetIcon src={chatIcon} size={20} className="sidebar__rail-icon" />
          </button>
          <button type="button" className="sidebar__rail-btn" aria-label="Search">
            <AssetIcon src={searchIcon} size={20} className="sidebar__rail-icon" />
          </button>
        </nav>
      )}
    </aside>
  );
}
